import { FeatureFlags } from '@logz-pkg/enums';

export const SLOW_REQUEST_THRESHOLD_MS = 3000;
export const SLOW_REQUEST_LOG_IDENTIFIER = 'SLOW_REQUEST';

// The logger ships through the same HTTP layer; reporting its own slow calls would loop.
const IGNORED_URL_PREFIXES = ['/logger/'];

export interface ISlowRequestReport {
  url: string;
  method: string;
  durationMs: number;
  statusCode?: number;
  /** The X-REQUEST-ID sent with the call; the same value the Node access log stores as `requestId`. */
  requestId?: string;
}

const isIgnoredUrl = (url: string): boolean => IGNORED_URL_PREFIXES.some(prefix => url.startsWith(prefix));

/*
 * The session state and the logger are imported lazily: both import chains reach `crud.api.service`, which imports
 * `request.provider`, which imports this file. A static import closes that cycle and every DAL service that builds
 * a `CrudApiService` at module load throws `CrudApiService is not a constructor`.
 */

/** False until the session loads, so the session call itself is never timed: the flag is unknown before it. */
const isEnabled = async (): Promise<boolean> => {
  const { sessionStateService } = await import('../../state/session.state.service');

  return sessionStateService.session.get()?.featureFlags?.includes(FeatureFlags.AppPerformanceMonitoring) === true;
};

const shouldReport = ({ url, durationMs }: ISlowRequestReport): boolean =>
  durationMs >= SLOW_REQUEST_THRESHOLD_MS && !isIgnoredUrl(url);

const report = async (slowRequest: ISlowRequestReport): Promise<void> => {
  if (!shouldReport(slowRequest)) return;

  const { url, method, durationMs, statusCode, requestId } = slowRequest;
  const loggerModule = await import('../logger/logger.service');

  // `url` is reserved by the logger for the page URL, hence the `request*` names.
  loggerModule.LoggerService.logWarn({
    message: `Slow request - ${method.toUpperCase()} ${url}`,
    extra: {
      logIdentifier: SLOW_REQUEST_LOG_IDENTIFIER,
      category: 'performance',
      requestId,
      requestUrl: url,
      requestMethod: method.toUpperCase(),
      requestDurationMs: Math.round(durationMs),
      requestStatusCode: statusCode,
    },
  });
};

export const slowRequestReporter = {
  isEnabled,
  shouldReport,
  report,
};
